// apps/web/src/pages/office/Tasks.jsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom'; // ✅ added
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, Search, FilterX, Eye, Edit, Trash2, RefreshCw,
  AlertCircle, User, Briefcase, Calendar, Clock, CheckCircle,
  XCircle, Loader2, FileText, Tag, Users, ArrowUpDown,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbaseClient';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

// ─── Helpers ──────────────────────────────────────────────────────
const getStatusBadge = (status) => {
  const config = {
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800 border-amber-200' },
    assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-800 border-blue-200' },
    'in-progress': { label: 'In Progress', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
    submitted: { label: 'Submitted', color: 'bg-purple-100 text-purple-800 border-purple-200' },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-800 border-green-200' },
  };
  return config[status] || { label: status || 'Unknown', color: 'bg-slate-100 text-slate-800 border-slate-200' };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

const STATUS_OPTIONS = ['all', 'pending', 'assigned', 'in-progress', 'submitted', 'completed'];

// ─── Main Component ──────────────────────────────────────────────
const Tasks = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate(); // ✅ defined
  const queryClient = useQueryClient();

  // ─── State ──────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [filterDesigner, setFilterDesigner] = useState('all');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // ─── Role check ──────────────────────────────────────────
  const userRole = currentUser?.role || 'sales';
  const isAdmin = ['admin', 'super_admin'].includes(userRole);
  const isManager = userRole === 'manager';
  const canManage = isAdmin || isManager;

  // ─── Fetch projects for filter & select ──────────────────
  const { data: projects = [] } = useQuery({
    queryKey: ['projects', 'all'],
    queryFn: () => pb.collection('projects').getFullList({ sort: 'name', fields: 'id,name' }),
    enabled: true,
  });

  // ─── Fetch designers (users with role designer/freelancer) ──
  const { data: designers = [] } = useQuery({
    queryKey: ['users', 'designers'],
    queryFn: () =>
      pb.collection('users').getFullList({
        sort: 'name',
        filter: 'role = "designer" || role = "freelancer"',
        fields: 'id,name,role',
      }),
    enabled: true,
  });

  // ─── Fetch tasks with expand ─────────────────────────────
  const {
    data: tasks = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tasks', 'office'],
    queryFn: () =>
      pb.collection('project_tasks').getFullList({
        sort: '-created',
        expand: 'project,assignedTo,submittedBy,completedBy',
      }),
    enabled: true,
  });

  // ─── Mutations ──────────────────────────────────────────
  const createTask = useMutation({
    mutationFn: (data) => pb.collection('project_tasks').create(data),
    onSuccess: () => {
      toast.success('✅ Task created');
      queryClient.invalidateQueries(['tasks', 'office']);
      setShowCreateModal(false);
    },
    onError: (err) => toast.error('Failed: ' + err.message),
  });

  const updateTask = useMutation({
    mutationFn: ({ id, data }) => pb.collection('project_tasks').update(id, data),
    onSuccess: () => {
      toast.success('✅ Task updated');
      queryClient.invalidateQueries(['tasks', 'office']);
      setShowEditModal(false);
      setEditingTask(null);
    },
    onError: (err) => toast.error('Failed: ' + err.message),
  });

  const deleteTask = useMutation({
    mutationFn: (id) => pb.collection('project_tasks').delete(id),
    onSuccess: () => {
      toast.success('🗑️ Task deleted');
      queryClient.invalidateQueries(['tasks', 'office']);
    },
    onError: (err) => toast.error('Failed to delete: ' + err.message),
  });

  // ─── Filtering ──────────────────────────────────────────
  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      const searchLower = search.toLowerCase();
      const matchesSearch =
        task.title?.toLowerCase().includes(searchLower) ||
        task.description?.toLowerCase().includes(searchLower) ||
        task.category?.toLowerCase().includes(searchLower);
      const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
      const matchesProject = filterProject === 'all' || task.project === filterProject;
      const matchesDesigner = filterDesigner === 'all' || task.assignedTo === filterDesigner;
      return matchesSearch && matchesStatus && matchesProject && matchesDesigner;
    });
  }, [tasks, search, filterStatus, filterProject, filterDesigner]);

  // ─── Handlers ──────────────────────────────────────────
  const handleCreate = (formData) => {
    createTask.mutate(formData);
  };

  const handleUpdate = (id, data) => {
    updateTask.mutate({ id, data });
  };

  const handleDelete = (id, title) => {
    if (window.confirm(`Delete task "${title}"? This cannot be undone.`)) {
      deleteTask.mutate(id);
    }
  };

  // ─── Loading / Error ──────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-red-200 p-8">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-semibold text-lg">⚠️ Failed to load tasks</p>
        <p className="text-sm text-slate-500 mb-4">{error.message}</p>
        <Button onClick={() => refetch()} className="gap-2">
          <RefreshCw className="w-4 h-4" /> Retry
        </Button>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-2">
              <FileText className="w-8 h-8" /> Project Tasks
            </h1>
            <p className="text-white/80">Manage tasks assigned to designers and freelancers</p>
          </div>
          <div className="flex gap-2">
            {canManage && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg"
              >
                <Plus className="w-5 h-5 mr-2" /> New Task
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => refetch()}
              className="bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search tasks..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUS_OPTIONS.filter(s => s !== 'all').map(s => (
                <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Project" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterDesigner} onValueChange={setFilterDesigner}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Designer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Designers</SelectItem>
              {designers.map(d => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(search || filterStatus !== 'all' || filterProject !== 'all' || filterDesigner !== 'all') && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setSearch('');
                setFilterStatus('all');
                setFilterProject('all');
                setFilterDesigner('all');
              }}
            >
              <FilterX className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Task Table */}
      {filteredTasks.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
          <p className="text-slate-500">No tasks found.</p>
        </div>
      ) : (
        <Card className="border-0 shadow-xl overflow-hidden">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <tr>
                  <th className="p-3 text-left font-semibold text-indigo-700">Title</th>
                  <th className="p-3 text-left font-semibold text-indigo-700">Project</th>
                  <th className="p-3 text-left font-semibold text-indigo-700">Assigned To</th>
                  <th className="p-3 text-left font-semibold text-indigo-700">Status</th>
                  <th className="p-3 text-left font-semibold text-indigo-700">Category</th>
                  <th className="p-3 text-left font-semibold text-indigo-700">Created</th>
                  <th className="p-3 text-center font-semibold text-indigo-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTasks.map((task) => {
                  const status = getStatusBadge(task.status);
                  const project = task.expand?.project;
                  const assigned = task.expand?.assignedTo;
                  return (
                    <tr key={task.id} className="hover:bg-indigo-50/50 transition-colors">
                      <td className="p-3 font-medium text-slate-800 max-w-xs truncate">{task.title}</td>
                      <td className="p-3">
                        {project ? (
                          <button
                            onClick={() => navigate(`/office/projects/${project.id}`)}
                            className="text-indigo-600 hover:underline"
                          >
                            {project.name}
                          </button>
                        ) : '—'}
                      </td>
                      <td className="p-3">
                        {assigned?.name || '—'}
                        {!task.assignedTo && <span className="text-amber-500 text-xs ml-1">(unassigned)</span>}
                      </td>
                      <td className="p-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="text-slate-600">
                          {task.category || 'General'}
                        </Badge>
                      </td>
                      <td className="p-3 text-slate-500">{formatDate(task.created)}</td>
                      <td className="p-3 text-center">
                        <div className="flex gap-1 justify-center">
                          <button
                            onClick={() => {
                              setEditingTask(task);
                              setShowEditModal(true);
                            }}
                            className="text-indigo-500 hover:text-indigo-700 p-1 rounded hover:bg-indigo-50"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {canManage && (
                            <>
                              <button
                                onClick={() => {
                                  setEditingTask(task);
                                  setShowEditModal(true);
                                }}
                                className="text-amber-500 hover:text-amber-700 p-1 rounded hover:bg-amber-50"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(task.id, task.title)}
                                className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Create Task Modal */}
      <CreateTaskModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        projects={projects}
        designers={designers}
        onSubmit={handleCreate}
        isSubmitting={createTask.isPending}
      />

      {/* Edit / View Task Modal */}
      {editingTask && (
        <EditTaskModal
          open={showEditModal}
          onOpenChange={setShowEditModal}
          task={editingTask}
          projects={projects}
          designers={designers}
          onSubmit={handleUpdate}
          isSubmitting={updateTask.isPending}
          canManage={canManage}
        />
      )}
    </div>
  );
};

// ─── Create Task Modal ──────────────────────────────────────────
const CreateTaskModal = ({ open, onOpenChange, projects, designers, onSubmit, isSubmitting }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    project: '',
    assignedTo: '',
    status: 'pending',
    category: '',
    floorNumber: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!form.project) {
      toast.error('Project is required');
      return;
    }
    onSubmit(form);
    setForm({ title: '', description: '', project: '', assignedTo: '', status: 'pending', category: '', floorNumber: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-indigo-700">Create New Task</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-slate-700">Title *</Label>
            <Input
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Task title"
              required
            />
          </div>
          <div>
            <Label className="text-slate-700">Description</Label>
            <Textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Task description..."
              rows={3}
            />
          </div>
          <div>
            <Label className="text-slate-700">Project *</Label>
            <Select
              value={form.project}
              onValueChange={(val) => handleSelect('project', val)}
            >
              <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700">Assign To (optional)</Label>
            <Select
              value={form.assignedTo}
              onValueChange={(val) => handleSelect('assignedTo', val)}
            >
              <SelectTrigger><SelectValue placeholder="Select designer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">Unassigned</SelectItem>
                {designers.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-700">Status</Label>
              <Select
                value={form.status}
                onValueChange={(val) => handleSelect('status', val)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Category</Label>
              <Input
                name="category"
                value={form.category}
                onChange={handleChange}
                placeholder="e.g., Design, Review"
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-700">Floor Number (optional)</Label>
            <Input
              name="floorNumber"
              value={form.floorNumber}
              onChange={handleChange}
              placeholder="e.g., Ground, 1st"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting} className="bg-indigo-600 hover:bg-indigo-700">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Edit / View Task Modal ─────────────────────────────────────
const EditTaskModal = ({ open, onOpenChange, task, projects, designers, onSubmit, isSubmitting, canManage }) => {
  const [form, setForm] = useState({ ...task });

  // reset when task changes
  React.useEffect(() => {
    if (task) setForm({ ...task });
  }, [task]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!form.project) {
      toast.error('Project is required');
      return;
    }
    const { id, created, updated, expand, ...updateData } = form;
    onSubmit(id, updateData);
  };

  const statusColor = getStatusBadge(form.status);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-indigo-700">{canManage ? 'Edit' : 'View'} Task</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-slate-700">Title *</Label>
            <Input
              name="title"
              value={form.title || ''}
              onChange={handleChange}
              placeholder="Task title"
              required
              disabled={!canManage}
            />
          </div>
          <div>
            <Label className="text-slate-700">Description</Label>
            <Textarea
              name="description"
              value={form.description || ''}
              onChange={handleChange}
              placeholder="Task description..."
              rows={3}
              disabled={!canManage}
            />
          </div>
          <div>
            <Label className="text-slate-700">Project *</Label>
            <Select
              value={form.project || ''}
              onValueChange={(val) => handleSelect('project', val)}
              disabled={!canManage}
            >
              <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700">Assigned To</Label>
            <Select
              value={form.assignedTo || ''}
              onValueChange={(val) => handleSelect('assignedTo', val)}
              disabled={!canManage}
            >
              <SelectTrigger><SelectValue placeholder="Select designer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">Unassigned</SelectItem>
                {designers.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-700">Status</Label>
              <Select
                value={form.status || 'pending'}
                onValueChange={(val) => handleSelect('status', val)}
                disabled={!canManage}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              {form.status && (
                <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium border ${statusColor.color}`}>
                  {statusColor.label}
                </span>
              )}
            </div>
            <div>
              <Label className="text-slate-700">Category</Label>
              <Input
                name="category"
                value={form.category || ''}
                onChange={handleChange}
                placeholder="e.g., Design, Review"
                disabled={!canManage}
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-700">Floor Number (optional)</Label>
            <Input
              name="floorNumber"
              value={form.floorNumber || ''}
              onChange={handleChange}
              placeholder="e.g., Ground, 1st"
              disabled={!canManage}
            />
          </div>
          {!canManage && (
            <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-500">
              <p>You are viewing this task in read‑only mode.</p>
              {form.submittedBy && (
                <p className="mt-1">Submitted by: {task.expand?.submittedBy?.name || 'Unknown'}</p>
              )}
              {form.completedBy && (
                <p>Completed by: {task.expand?.completedBy?.name || 'Unknown'}</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
              {canManage ? 'Cancel' : 'Close'}
            </Button>
            {canManage && (
              <Button type="submit" disabled={isSubmitting} className="bg-indigo-600 hover:bg-indigo-700">
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Tasks;